/* External */
import {
  List,
  Map,
  Record,
  is,
  isCollection,
} from 'immutable';
import mapValues from 'lodash-es/mapValues';

/* Ott-common */
import { TypeDefType } from '@dtvgo/typed-normalizr';

/* Utils */
import { mergeDeepWithLists, shallowEqualLists } from '../../utils/functions';

function getCreator(definition) {
  if (definition.isTypeDef) {
    const { type, defaultValue } = definition;

    switch (type) {
      case TypeDefType.ARRAY: {
        return List;
      }
      case TypeDefType.OBJECT: {
        return Map;
      }
      case TypeDefType.SHAPE: {
        return Record(defaultValue);
      }
      default: {
        return undefined;
      }
    }
  }

  return undefined;
}

export default function createModel(TypedNormalizrEntity, name, mergeStrategies = {}) {
  const { defaultValues, definition } = TypedNormalizrEntity;
  const creators = mapValues(definition, getCreator);
  const newDefaultValues = mapValues(creators, (creator, key) => {
    if (creator) {
      return undefined;
    }
    return defaultValues[key];
  });

  const RecordFactory = Record(newDefaultValues, name);

  const Model = class extends RecordFactory {
    get normalId() {
      return Model.getId(this?.toJS());
    }

    setAll(newValues) {
      return (this.mergeWith((oldValue, newValue, key) => {
        if (mergeStrategies[key]) {
          return mergeStrategies[key](oldValue, newValue);
        }

        if (newValue === undefined || newValue === null || newValue === '') {
          return oldValue;
        }

        let newCreatedValue = newValue;
        const creator = creators[key];
        if (creator) {
          newCreatedValue = creator(newValue);
        }

        if (!oldValue) {
          return newCreatedValue;
        }

        if (oldValue.mergeDeepWith) {
          return mergeDeepWithLists(oldValue, newCreatedValue);
        }

        if (
          isCollection(oldValue)
          && isCollection(newCreatedValue)
          && shallowEqualLists(oldValue, newCreatedValue)
        ) {
          return oldValue;
        }

        const areEqual = definition[key]?.areEqual || is;
        if (areEqual(oldValue, newCreatedValue)) {
          return oldValue;
        }

        return newCreatedValue;
      }, newValues));
    }

    static getId(entity) {
      let normalId = entity.id;
      if (typeof TypedNormalizrEntity.idAttribute === 'string') {
        normalId = entity[TypedNormalizrEntity.idAttribute];
      }
      if (typeof TypedNormalizrEntity.idAttribute === 'function') {
        normalId = TypedNormalizrEntity.idAttribute(entity);
      }

      return normalId;
    }
  };

  const { name: entityName, idAttribute } = TypedNormalizrEntity;
  Model.entityName = entityName;
  Model.idAttribute = idAttribute;

  return Model;
}
